import React, { useCallback, useEffect, useRef, useState } from "react";
import Select from "./components/Select";
import Spacer from "./components/Spacer";
import Divider from "./components/Divider";
import Button from "../common/Button";
import Collapsible from "react-collapsible";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleDown } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import Input from "./components/Input";
import NitrogenApplicationSection from "./NitrogenApplicationSection";
import ManureApplicationSection from "./ManureApplicationSection";
import { LOCAL_PREFIX } from "../../utils/constants";
import Toggle from "../common/Toggle";
import ConfirmModal from "../common/ConfirmModal";
import { getHighestLevelWithPrefix } from "./utils/parseProblems";
import { levelToColor, levelToIconName } from "./components/ProblemIndicator";

library.add(faChevronCircleDown);

const CROP_OPTIONS = [
  "Alfalfa",
  "Corn",
  "Dry Beans",
  "Fallow",
  "Field Beans",
  "Grain Sorghum",
  "Grass",
  "Millet",
  "Oats",
  "Other",
  "Popcorn",
  "Potatoes",
  "Silage Corn",
  "Small Grains",
  "Sorghum / Sudan Grass",
  "Sorghum Sudan Grass",
  "Soybeans",
  "Sugar Beets",
  "Sunflowers",
  "Wheat",
];

const CROP_UNIT_OPTIONS = ["bushels / ac", "pounds / ac", "tons / ac"];

const TILLAGE_OPTIONS = ["No Till", "Reduced Till", "Strip Till", "Conventional Till"];

function getConfirmMessage(toDelete) {
  if (toDelete.isSaved) {
    return `This will remove the crop "${toDelete.name}". After saving you will not be able to recover this change. If unsaved, you can reload the page to recover this crop.`;
  }

  return `This will permanently remove the crop "${toDelete.name}". This cannot be undone.`;
}

export default function CropReportItem({
  index, crop, isDryLand, checkDirty, onRemove, formProblems, setFormProblems,
}) {
  const colorKey = crop.color;

  const [name, setName] = useState(crop.name ?? '');

  const [cropYield, setCropYield] = useState(crop.yield ?? '');
  const [yieldGoal, setYieldGoal] = useState(crop.yieldGoal ?? '');
  const [hasDamage, setHasDamage] = useState(!!crop.hasDamage);

  const [units, setUnits] = useState(crop.yieldUnits ?? '');

  const [sprinkler, setSprinkler] = useState(crop.irrigation.sprinkler ?? '');
  const [flood, setFlood] = useState(crop.irrigation.flood ?? '');
  const [other, setOther] = useState(crop.irrigation.other ?? '');

  const [irrigationApplied, setIrrigationApplied] = useState(crop.irrigationApplied ?? '');
  const [waterNitrate, setWaterNitrate] = useState(crop.waterNitrate ?? '');
  const [tillage, setTillage] = useState(crop.tillage ?? '');

  const [note, setNote] = useState(crop.note ?? '');

  // Keep the index increasing for use with keys.
  const localIdTracker = useRef(0);

  const [nitrogenApplications, setNitrogenApplications] = useState(() => {
    return crop.nitrogenApplications;
  });

  const [manureApplications, setManureApplications] = useState(() => {
    return crop.manureApplications;
  });

  useEffect(() => {
    if (waterNitrate === "" && waterNitrate !== crop.waterNitrate) {
      setWaterNitrate(crop.waterNitrate);
    }
  }, [crop.waterNitrate]);

  const [toDelete, setToDelete] = useState(undefined);

  const setIrrigation = useCallback((type, value) => {
    switch (type) {
      case 'sprinkler':
        setSprinkler(value);
        break;
      case 'flood':
        setFlood(value);
        break;
      case 'other':
      default:
        setOther(value);
        break;
    }

    const i = formProblems.findIndex((p) => p.id === "total-tract-irrigation");
    if (i >= 0) {
      formProblems.splice(i, 1);
    }
  }, [formProblems]);

  const addNitrogenApplication = useCallback(() => {
    setNitrogenApplications((n) => [...n, { id: `${LOCAL_PREFIX}-${localIdTracker.current}` }]);
    localIdTracker.current += 1;
  }, []);

  const addManureApplication = useCallback(() => {
    setManureApplications((n) => [...n, { id: `${LOCAL_PREFIX}-${localIdTracker.current}` }]);
    localIdTracker.current += 1;
  }, []);

  const setAndClearProblem = useCallback((setter, value, field) => {
    const i = formProblems.findIndex((p) => p.field === field);
    if (i >= 0) {
      formProblems.splice(i, 1);
    }

    setter(value);
  }, [formProblems]);

  useEffect(() => {
    checkDirty({
      id: crop.id,
      color: crop.color,
      year: crop.year,
      name,
      yield: cropYield,
      yieldGoal: name === 'Corn' ? yieldGoal : '',
      yieldUnits: units,
      irrigation: { sprinkler, flood, other },
      irrigationApplied,
      waterNitrate,
      tillage,
      manureApplications,
      nitrogenApplications,
      hasDamage,
      note,
    });
  }, [
    name, cropYield, yieldGoal, units, sprinkler, flood, other, irrigationApplied,
    waterNitrate, tillage, nitrogenApplications, manureApplications, hasDamage, note, checkDirty,
  ]);

  const problemLevel = getHighestLevelWithPrefix(formProblems, crop.id, true);

  return (<>
    <Collapsible
      className="w-full bg-zinc-300 p-3 rounded-3xl"
      openedClassName="w-full bg-zinc-300 p-3 rounded-3xl"
      contentOuterClassName="w-full flex flex-col"
      contentInnerClassName="flex-1 ml-9"
      triggerClassName="Collapsible__trigger group"
      triggerOpenedClassName="Collapsible__trigger group"
      trigger={(
        <div className="flex gap-2.5 items-center">
          <FontAwesomeIcon
            icon="chevron-circle-down"
            size="xl"
            style={{ color: colorKey }}
            className="group-[.is-closed]:-rotate-90 transition-all"
          />
          <h2 className="text-lg font-bold">
            Crop {index + 1}
            {name && ` - ${name}`}
            {crop.failedToSave && (
              <FontAwesomeIcon
                icon="exclamation-circle"
                className="ml-1 text-red-500"
                data-tooltip-id="unsaved-crop-tooltip"
              />
            )}
            {!crop.failedToSave && problemLevel !== "" && (
              <FontAwesomeIcon
                icon={levelToIconName(problemLevel)}
                className={`ml-1 ${levelToColor(problemLevel)}`}
                data-tooltip-id="invalid-crop-tooltip"
              />
            )}
          </h2>
          <Spacer />
          <button
            title="Remove this crop"
            // Create some hit slop for the button.
            className="h-7 w-7 p-1 group-[.is-closed]:hidden rounded-lg text-white text-lg mr-2"
            onClickCapture={(event) => {
              event.stopPropagation();
              // TODO: Could skip this and just remove if all fields are empty.
              setToDelete({
                name: name || `Crop ${index + 1}`,
                confirm: onRemove,
                isSaved: !crop.id.startsWith(LOCAL_PREFIX),
              });
            }}
          >
            <div
              className="bg-red-500 h-5 w-5 rounded-full flex items-center justify-center"
            >
              -
            </div>
          </button>
        </div>
      )}

      transitionTime={150}
      open
    >
      <h3 className="text-base font-bold">Crop Report Year and Crop Type</h3>

      <div className="flex gap-4">
        <Select
          placeholder="Select a Crop"
          name={`${crop.id}-crop-name`}
          label={'Crop Planted'}
          value={name}
          options={CROP_OPTIONS.map((v) => ({ label: v, value: v }))}
          onChange={({ value }) => setAndClearProblem(setName, value, `${crop.id}-crop-name`)}
        />
        <div
          className="flex-1 flex flex-col"
          title="Year that the crop was harvested"
        >
          <span>Crop Report Year</span>
          <span className="p-1.5 bg-stone-300 rounded border border-stone-400">{crop.year}</span>
        </div>
      </div>
      <div className="flex gap-4">
        <Input
          label="Crop Yield"
          type="number"
          name={`${crop.id}-crop-yield`}
          description="Yield of the crop at harvest"
          value={cropYield}
          onChange={(v) => setAndClearProblem(setCropYield, v, `${crop.id}-crop-yield`)}
          formErrors={formProblems}
        />
        <Select
          label="Crop Yield Units"
          name={`${crop.id}-crop-units`}
          value={units}
          options={CROP_UNIT_OPTIONS.map((v) => ({ label: v, value: v }))}
          onChange={({ value }) => setAndClearProblem(setUnits, value, `${crop.id}-crop-units`)}
          formErrors={formProblems}
        />
      </div>
      <div className="flex gap-4">
        {name === 'Corn' && <>
          <Input
            label="Corn Yield Goal (bushels / ac)"
            type="number"
            name={`${crop.id}-crop-yield-goal`}
            value={yieldGoal}
            onChange={(v) => setAndClearProblem(setYieldGoal, v, `${crop.id}-crop-yield-goal`)}
            formErrors={formProblems}
          />
        </>}
        <div className="flex-1 flex items-center pt-2">
          <Toggle
            label="Crop Damage"
            name={`${crop}-damage`}
            value={hasDamage}
            onChange={setHasDamage}
          />
        </div>
      </div>

      <Divider />

      <div className="flex gap-4">
        {!isDryLand && (<>
          <Input
            label="Sprinkler Acres"
            type="number"
            name={`${crop.id}-crop-sprinkler`}
            value={sprinkler}
            onChange={(value) => setIrrigation('sprinkler', value)}
            formErrors={formProblems}
          />
          <Input
            label="Flood Acres"
            type="number"
            name={`${crop.id}-crop-flood`}
            value={flood}
            onChange={(value) => setIrrigation('flood', value)}
            formErrors={formProblems}
          />
        </>)}
        <Input
          label={isDryLand ? "Dry Land Acres" : "Other Acres"}
          type="number"
          name={`${crop.id}-crop-other`}
          value={other}
          onChange={(value) => setIrrigation('other', value)}
          formErrors={formProblems}
        />
        {isDryLand && <Spacer />}
      </div>

      <Divider />

      <div className="flex gap-4">
        <Input
          label="Irrigation Water Applied"
          type="number"
          name={`${crop.id}-crop-irr-applied`}
          suffix="inches"
          value={irrigationApplied}
          onChange={(v) => setAndClearProblem(setIrrigationApplied, v, `${crop.id}-crop-irr-applied`)}
          formErrors={formProblems}
        />
        <Input
          label="Water Nitrate"
          type="number"
          name={`${crop.id}-crop-water-nitrate`}
          suffix="ppm NO-3"
          value={waterNitrate}
          onChange={(v) => setAndClearProblem(setWaterNitrate, v, `${crop.id}-crop-water-nitrate`)}
          formErrors={formProblems}
        />
      </div>

      <div>
        <Select
          label="Tillage Practice"
          name={`${crop.id}-crop-tillage`}
          options={TILLAGE_OPTIONS.map((v) => ({ label: v, value: v }))}
          value={tillage}
          onChange={({ value }) => setTillage(value)}
          formErrors={formProblems}
        />
      </div>

      <Divider />

      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold">Nitrogen Applications</h3>
        <div className="flex-1 h-px border-b border-dashed border-slate-500" />
        <Button className="h-7" onClick={addNitrogenApplication}>Add Application Event</Button>
      </div>

      <div className="flex flex-col gap-2 py-1">
        {nitrogenApplications.filter((n) => !n.deleted).map((n, i) => (
          <NitrogenApplicationSection
            key={`nitro_app_${crop.id}_${n.id}`}
            index={i}
            crop={crop}
            canRemoveFirst={name !== "Corn"}
            initialData={n}
            formProblems={formProblems}
            onChange={(application) => {
              setNitrogenApplications((apps) => {
                const index = apps.findIndex((a) => a.id === n.id);
                apps.splice(index, 1, application);
                return [...apps];
              });
            }}
            onRemove={() => {
              // Remove the problems associated with this application.
              setFormProblems(formProblems.filter((p) => !p.field?.startsWith(`${crop.id}-nitro-${n.id}`)));
              setNitrogenApplications((prev) => {
                if (n.id.startsWith(LOCAL_PREFIX)) {
                  // This was only local, so we can just remove it from the list.
                  return prev.filter((item) => item !== n);
                } else {
                  // This was saved at some point, so we have to mark it deleted.
                  n.deleted = true;
                  return [...prev];
                }
              });
            }}
          />
        ))}
        {nitrogenApplications.length === 0 && (
          <div className="self-center py-4 flex flex-col items-center">
            <div className="mb-4">No nitrogen applications tracked</div>
            <Button className="text-base" onClick={addNitrogenApplication}>Add Application Event</Button>
          </div>
        )}
      </div>

      <Divider />

      <div className="flex items-center gap-2">
        <h3 className="text-base font-bold">Manure Applications</h3>
        <div className="flex-1 h-px border-b border-dashed border-slate-500" />
        <Button className="h-7" onClick={addManureApplication}>Add Application Event</Button>
      </div>

      <div className="flex w-full flex-col gap-2 py-1">
        {manureApplications.filter((m) => !m.deleted).map((m, i) => (
          <ManureApplicationSection
            key={`manure_app_${crop.id}_${m.id}`}
            index={i}
            crop={crop}
            initialData={m}
            formProblems={formProblems}
            onChange={(application) => {
              setManureApplications((apps) => {
                const index = apps.findIndex((a) => a.id === m.id);
                apps.splice(index, 1, application);
                return [...apps];
              });
            }}
            onRemove={() => {
              setFormProblems(formProblems.filter((p) => !p.field?.startsWith(`${crop.id}-manure-${m.id}`)));
              setManureApplications((prev) => {
                if (m.id.startsWith(LOCAL_PREFIX)) {
                  // This was only local, so we can just remove it from the list.
                  return prev.filter((item) => item !== m);
                } else {
                  // This was saved at some point, so we have to mark it deleted.
                  m.deleted = true;
                  return [...prev];
                }
              })
            }}
          />
        ))}
        {manureApplications.length === 0 && (
          <div className="self-center py-4 flex flex-col items-center">
            <div className="mb-4">No manure applications tracked</div>
            <Button className="text-base" onClick={addManureApplication}>Add Application Event</Button>
          </div>
        )}
      </div>

      <Divider />

      <h3 className="text-base font-bold">Notes</h3>
      <Input
        type="textarea"
        name={`${crop.id}-note`}
        value={note}
        onChange={setNote}
      />
    </Collapsible>
    <ConfirmModal
      isOpen={!!toDelete}
      title="Confirm Delete Crop"
      message={toDelete ? getConfirmMessage(toDelete) : ""}
      confirmButtonLabel="Yes, Delete"
      toggle={() => setToDelete(undefined)}
      onConfirm={() => {
        toDelete.confirm();
        setToDelete(undefined);
      }}
      secondaryButtonText="Cancel"
      warning
    />
  </>);
}
